import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/api';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#38bdf8', '#FDE047', '#10b981', '#FDE68A', '#f59e0b', '#ef4444', '#06b6d4', '#047857', '#84cc16', '#F59E0B'];

const StatCard = ({ label, value, icon, color }) => (
  <div data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`} style={{
    background: `linear-gradient(135deg, ${color}15, ${color}08)`,
    border: `1px solid ${color}30`,
    borderRadius: 12, padding: '16px 20px', textAlign: 'center', minWidth: 140
  }}>
    <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: 4 }}>
      <i className={icon} style={{ marginRight: 4, color }} />{label}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
  </div>
);

const PlatformAnalyticsDashboard = ({ showToast }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/admin/platform-analytics`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = () => {
      setLoading(false);
      if (x.status < 300) {
        try { setData(JSON.parse(x.responseText)); } catch (e) { /* ignore */ }
      }
    };
    x.onerror = () => setLoading(false);
    x.send();
  }, [token]);

  useEffect(() => { if (token) fetchAnalytics(); }, [token, fetchAnalytics]);

  if (loading) return <div style={{ color: '#a1a1aa', padding: 40, textAlign: 'center' }}>Loading analytics...</div>;
  if (!data) return <div style={{ color: '#ef4444', padding: 40, textAlign: 'center' }}>Failed to load analytics</div>;

  const { totals, engagement, filter_stats, user_growth, search_activity, category_growth, article_types, top_searchers } = data;

  return (
    <div data-testid="platform-analytics-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <h3 style={{ color: '#38bdf8', margin: 0, fontSize: '1.2rem' }}>
        <i className="fas fa-chart-line" style={{ marginRight: 8 }} />Platform Analytics
      </h3>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
        <StatCard label="Total Users" value={totals.users} icon="fas fa-users" color="#38bdf8" />
        <StatCard label="Categories" value={totals.categories} icon="fas fa-folder" color="#FDE047" />
        <StatCard label="URLs Indexed" value={totals.urls} icon="fas fa-link" color="#10b981" />
        <StatCard label="Public Categories" value={totals.public_categories} icon="fas fa-globe" color="#FDE68A" />
        <StatCard label="Marketplace" value={totals.marketplace_protocols} icon="fas fa-store" color="#f59e0b" />
        <StatCard label="Active (7d)" value={engagement.active_7d} icon="fas fa-bolt" color="#06b6d4" />
        <StatCard label="Active (30d)" value={engagement.active_30d} icon="fas fa-calendar" color="#047857" />
        <StatCard label="Achievers" value={engagement.users_with_achievements} icon="fas fa-trophy" color="#F59E0B" />
      </div>

      {/* User Growth Chart */}
      <div style={{ background: '#475569', borderRadius: 12, padding: 20, border: '1px solid rgba(253, 230, 138, 0.15)' }}>
        <h4 style={{ color: '#38bdf8', margin: '0 0 12px', fontSize: '0.95rem' }}>
          <i className="fas fa-user-plus" style={{ marginRight: 6 }} />User Registrations (Last 30 Days)
        </h4>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={user_growth}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
            <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} interval={4} />
            <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#1e1b2e', border: '1px solid rgba(253, 230, 138,0.3)', borderRadius: 8, color: '#334155' }} />
            <Area type="monotone" dataKey="users" stroke="#38bdf8" fill="rgba(253, 230, 138,0.15)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Search Activity & Category Growth Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#475569', borderRadius: 12, padding: 20, border: '1px solid rgba(16, 185, 129, 0.15)' }}>
          <h4 style={{ color: '#10b981', margin: '0 0 12px', fontSize: '0.95rem' }}>
            <i className="fas fa-search" style={{ marginRight: 6 }} />Search Activity
          </h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={search_activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 9 }} interval={6} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#1e1b2e', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, color: '#334155' }} />
              <Bar dataKey="searches" fill="rgba(16,185,129,0.6)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: '#475569', borderRadius: 12, padding: 20, border: '1px solid rgba(252, 211, 77, 0.15)' }}>
          <h4 style={{ color: '#047857', margin: '0 0 12px', fontSize: '0.95rem' }}>
            <i className="fas fa-folder-plus" style={{ marginRight: 6 }} />Category Creation
          </h4>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={category_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.85)" />
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 9 }} interval={6} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#1e1b2e', border: '1px solid rgba(252, 211, 77,0.3)', borderRadius: 8, color: '#334155' }} />
              <Area type="monotone" dataKey="categories" stroke="#047857" fill="rgba(252, 211, 77,0.12)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Article Types & Filter Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Article Types Pie Chart */}
        <div style={{ background: '#475569', borderRadius: 12, padding: 20, border: '1px solid rgba(253, 230, 138, 0.15)' }}>
          <h4 style={{ color: '#047857', margin: '0 0 12px', fontSize: '0.95rem' }}>
            <i className="fas fa-chart-pie" style={{ marginRight: 6 }} />Content Types
          </h4>
          {article_types && article_types.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={article_types} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {article_types.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e1b2e', border: '1px solid rgba(253, 230, 138,0.3)', borderRadius: 8, color: '#334155' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#71717a', textAlign: 'center', padding: 40 }}>No content type data yet</div>
          )}
        </div>

        {/* Filter Usage Stats */}
        <div style={{ background: '#475569', borderRadius: 12, padding: 20, border: '1px solid rgba(245, 158, 11, 0.15)' }}>
          <h4 style={{ color: '#f59e0b', margin: '0 0 16px', fontSize: '0.95rem' }}>
            <i className="fas fa-sliders-h" style={{ marginRight: 6 }} />Filter Usage
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Saved Presets', value: filter_stats.total_saves, color: '#38bdf8', icon: 'fas fa-save' },
              { label: 'Shared Presets', value: filter_stats.shared_presets, color: '#047857', icon: 'fas fa-share-alt' },
              { label: 'History Entries', value: filter_stats.history_entries, color: '#10b981', icon: 'fas fa-history' }
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: `${s.color}08`, borderRadius: 8, border: `1px solid ${s.color}20` }}>
                <span style={{ color: '#334155', fontSize: '0.85rem' }}><i className={s.icon} style={{ marginRight: 8, color: s.color }} />{s.label}</span>
                <span style={{ color: s.color, fontWeight: 700, fontSize: '1.1rem' }}>{s.value.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Top Searchers */}
          {top_searchers && top_searchers.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h5 style={{ color: '#f59e0b', fontSize: '0.85rem', margin: '0 0 10px' }}>Top Searchers</h5>
              {top_searchers.slice(0, 3).map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', fontSize: '0.78rem', color: '#a1a1aa' }}>
                  <span>#{i + 1} {s.user_id?.substring(0, 10)}...</span>
                  <span style={{ color: '#f59e0b' }}>{s.count.toLocaleString()} results</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <button onClick={fetchAnalytics} data-testid="refresh-analytics-btn" style={{
        background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.08))',
        border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8',
        padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, alignSelf: 'flex-start'
      }}>
        <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />Refresh Analytics
      </button>
    </div>
  );
};

export default PlatformAnalyticsDashboard;
